import { useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { loadStripe, type Stripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { Btn } from "@/components/ui/Btn/Btn";
import { Icon } from "@/components/ui/Icon/Icon";
import { Modal } from "@/components/ui/Modal/Modal";
import { AppShell } from "@/components/app/AppShell";
import { useAuth } from "@/lib/AuthContext";
import { getConfig } from "@/lib/env";
import {
  useBilling,
  useCancelSubscription,
  useChangePlan,
  useCreatePaymentMethod,
  useSubscribe,
  useWorkspace,
  type BillingState,
} from "@/lib/queries";
import { useIsMobile } from "@/lib/useMediaQuery";
import {
  classifyPlanChange,
  PLANS,
  type Cadence,
  type PlanId,
} from "@notamen/core/billing";
import styles from "./Billing.module.css";

/**
 * Workspace billing surface (route /w/:ws/billing). Owner-gated, mirrors the
 * /w/:ws/members + /w/:ws/sso pattern. Two sections:
 *
 *  1. Plan & subscription — the self-serve subscription switcher (subscribe /
 *     upgrade / downgrade / cancel across solo/studio/agency, monthly+annual),
 *     reflecting the live `subscription` block from GET /billing. Owner-only
 *     mutations (the API re-checks); non-owners see read-only current state.
 *  2. Media usage — read-only allowance / used / remaining / packs + the
 *     accrued running tab the workspace owes at month-end, plus the existing
 *     owner-only "add a payment method" flow via Stripe Elements + a SetupIntent.
 *
 * Honesty contract:
 * - Subscriptions CHARGE the saved card immediately (no trial) — copy says so.
 *   Upgrades charge the prorated difference now; downgrades take effect at the
 *   period end; cancel keeps access until the period end, then the workspace
 *   locks. Every string describes what the backend actually does.
 * - Media money language is "accrued/owed", never "charged"; a SetupIntent only
 *   saves a card.
 * - Fail-closed: Elements only mounts when a non-empty Stripe publishable key
 *   is configured AND the API reports payments configured. Subscribe/upgrade/
 *   downgrade REQUIRE a card on file first — without one the action opens the
 *   add-card flow rather than firing a doomed request.
 * - The client never computes a charge; it renders server-provided numbers.
 */

/** Stripe singleton, created lazily from the runtime publishable key. */
let stripePromise: Promise<Stripe | null> | null = null;
function getStripe(pk: string): Promise<Stripe | null> {
  if (!stripePromise) stripePromise = loadStripe(pk);
  return stripePromise;
}

function centsToDollars(cents: number): string {
  return (cents / 100).toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
  });
}

/** Whole-dollar plan price (no cents — the published prices are integers). */
function dollars(amount: number): string {
  return amount.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}

/** Format an ISO timestamp as a plain calendar date, or a fallback. */
function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "the end of the current period";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "the end of the current period";
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function planName(plan: PlanId | null | undefined): string {
  if (!plan) return "—";
  return PLANS[plan]?.name ?? plan;
}

/** The three self-serve tiers, in upgrade order. */
const SELF_SERVE_PLANS: PlanId[] = ["solo", "studio", "agency"];

export function Billing() {
  const { ws } = useParams<{ ws: string }>();
  const auth = useAuth();
  const workspaceQ = useWorkspace(ws);
  const billingQ = useBilling(ws);

  const [addOpen, setAddOpen] = useState(false);
  // After the add-card flow closes, optionally resume the action the user was
  // trying to take (subscribe/upgrade/downgrade) once a card lands.

  if (auth.status === "anonymous") {
    return <Navigate to={`/signin?next=/w/${ws}/billing`} replace />;
  }

  const callerRole = workspaceQ.data?.role;
  const isOwner = callerRole === "owner";
  const billing = billingQ.data;
  const pk = getConfig().stripePublishableKey ?? "";
  const workspaceName = workspaceQ.data?.workspace.name;

  return (
    <AppShell currentWorkspaceId={ws}>
      <div className={styles.page}>
        <Btn href={`/w/${ws}`} variant="ghost" size="sm" iconLeft={<Icon name="arrow-left" size={13} />}>
          Back to workspace
        </Btn>

        <header className={styles.head}>
          <p className={styles.kicker}>Workspace</p>
          <h1 className={styles.title}>
            <Icon name="store" size={20} /> Billing{workspaceName ? ` · ${workspaceName}` : ""}
          </h1>
          <p className={styles.sub}>
            Manage your plan and subscription, and see the media-generation
            running tab your workspace has accrued this month.
          </p>
        </header>

        {billingQ.isLoading && <p className={styles.muted}>Loading billing…</p>}
        {billingQ.error && (
          <p className={styles.error}>Couldn’t load billing: {billingQ.error.message}</p>
        )}

        {billing && (
          <>
            {billing.delinquent && (
              <div className={styles.delinquent}>
                <Icon name="info" size={16} />
                <div>
                  <strong>A recent charge didn’t go through.</strong>
                  <p>
                    Update your payment method to keep generating. New media
                    generation is paused until a charge succeeds.
                  </p>
                </div>
              </div>
            )}

            <SubscriptionSection
              ws={ws}
              billing={billing}
              isOwner={isOwner}
              hasKey={pk.length > 0}
              onNeedCard={() => setAddOpen(true)}
            />

            <BillingStateCards billing={billing} />

            <PaymentMethodSection
              billing={billing}
              isOwner={isOwner}
              hasKey={pk.length > 0}
              onAdd={() => setAddOpen(true)}
            />
          </>
        )}

        {addOpen && billing && (
          <AddPaymentMethodModal ws={ws} pk={pk} onClose={() => setAddOpen(false)} />
        )}
      </div>
    </AppShell>
  );
}

// ─── Plan & subscription ───────────────────────────────────────────────────

/**
 * The self-serve subscription switcher. Reflects the live `subscription` block
 * from GET /billing and drives subscribe / change-plan / cancel. Owner-only
 * controls; non-owners see the read-only current state. Require-card-first:
 * when no card is on file, the per-tier action opens the add-card flow instead
 * of firing the request.
 */
function SubscriptionSection({
  ws,
  billing,
  isOwner,
  hasKey,
  onNeedCard,
}: {
  ws: string | undefined;
  billing: BillingState;
  isOwner: boolean;
  hasKey: boolean;
  onNeedCard: () => void;
}) {
  const sub = billing.subscription;
  const isMobile = useIsMobile();
  // The cadence the switcher prices/acts on. Default to the active cadence
  // when subscribed, else monthly.
  const [cadence, setCadence] = useState<Cadence>(sub.cadence ?? "monthly");
  const [cancelOpen, setCancelOpen] = useState(false);

  const subscribe = useSubscribe(ws);
  const changePlan = useChangePlan(ws);

  // Surface the most recent mutation error inline (server `message`).
  const actionError = subscribe.error ?? changePlan.error;
  const isPending = subscribe.isPending || changePlan.isPending;
  // Whether the change-plan error was a downgrade-over-limits block, so we can
  // prefix it with the "remove members/brands first" guidance.
  const isLimitError =
    !!changePlan.error &&
    typeof changePlan.error.body === "object" &&
    changePlan.error.body !== null &&
    (changePlan.error.body as { error?: string }).error === "plan_limit_exceeded";

  // Payments must be set up for this deployment for any mutation to work; the
  // backend returns 503 otherwise. When it isn't, render an honest read-only
  // state rather than buttons that can only fail.
  const paymentsUsable = billing.paymentsConfigured && hasKey;

  function actOnTier(plan: PlanId) {
    if (!ws) return;
    // Require a card first for any charge-bearing action. Without one, open the
    // add-card flow; the user re-clicks once the card lands (the billing query
    // re-fetches hasPaymentMethod on success).
    if (!billing.hasPaymentMethod) {
      onNeedCard();
      return;
    }
    if (!sub.active) {
      subscribe.mutate({ plan, cadence });
    } else {
      changePlan.mutate({ plan, cadence });
    }
  }

  return (
    <section className={styles.subscription}>
      <h2 className={styles.sectionTitle}>Plan &amp; subscription</h2>

      <CurrentSubscriptionState billing={billing} />

      {isOwner ? (
        <>
          {!paymentsUsable && (
            <p className={styles.muted}>
              Subscriptions aren’t available on this deployment yet — Stripe
              isn’t configured here.
            </p>
          )}

          {paymentsUsable && !billing.hasPaymentMethod && (
            <div className={styles.cardFirst}>
              <Icon name="info" size={15} />
              <span>Add a card to subscribe.</span>
              <Btn variant="primary" size="sm" onClick={onNeedCard} iconLeft={<Icon name="store" size={13} />}>
                Add a card
              </Btn>
            </div>
          )}

          <CadenceToggle cadence={cadence} onChange={setCadence} />

          {actionError && (
            <p className={styles.error}>
              {isLimitError
                ? `Remove members/brands first: ${actionError.message}`
                : actionError.message}
            </p>
          )}

          <div
            className={styles.tierGrid}
            style={isMobile ? { gridTemplateColumns: "1fr" } : undefined}
          >
            {SELF_SERVE_PLANS.map((plan) => (
              <TierCard
                key={plan}
                plan={plan}
                cadence={cadence}
                sub={sub}
                disabled={!paymentsUsable || isPending}
                onAct={() => actOnTier(plan)}
              />
            ))}
            <EnterpriseTierCard />
          </div>

          {sub.active && (
            <div className={styles.cancelRow}>
              <Btn
                variant="ghost"
                size="sm"
                onClick={() => setCancelOpen(true)}
                iconLeft={<Icon name="x-circle" size={14} />}
              >
                Cancel subscription
              </Btn>
            </div>
          )}
        </>
      ) : (
        <p className={styles.ownerNote}>Only a workspace owner can manage the subscription.</p>
      )}

      {cancelOpen && (
        <CancelModal ws={ws} sub={sub} onClose={() => setCancelOpen(false)} />
      )}
    </section>
  );
}

/** Read-only summary of the current subscription state (everyone sees this). */
function CurrentSubscriptionState({ billing }: { billing: BillingState }) {
  const sub = billing.subscription;

  if (!sub.has || (!sub.active && !sub.locked)) {
    return (
      <div className={styles.currentState}>
        <span className={styles.currentPlan}>No active subscription</span>
        <p className={styles.cardNote}>
          You’re on the {planName(billing.plan)} plan. Subscribe below to start a
          paid plan — your saved card is charged immediately (no trial).
        </p>
      </div>
    );
  }

  if (sub.locked) {
    return (
      <div className={styles.currentState}>
        <span className={styles.currentPlan}>Subscription ended</span>
        <p className={styles.cardNote}>
          Your {planName(sub.plan)} subscription has ended and this workspace is
          locked. Subscribe below to restore access.
        </p>
      </div>
    );
  }

  // Active.
  const cadenceLabel = sub.cadence === "annual" ? "billed annually" : "billed monthly";
  return (
    <div className={styles.currentState}>
      <span className={styles.currentPlan}>
        {planName(sub.plan)} · {cadenceLabel}
      </span>
      {sub.cancelAtPeriodEnd ? (
        <p className={styles.cardNote}>
          Cancels on {fmtDate(sub.currentPeriodEnd)} — access locks then.
        </p>
      ) : sub.scheduledPlan ? (
        <p className={styles.cardNote}>
          {planName(sub.plan)} until {fmtDate(sub.currentPeriodEnd)}, then{" "}
          {planName(sub.scheduledPlan)}.
        </p>
      ) : (
        <p className={styles.cardNote}>
          Renews on {fmtDate(sub.currentPeriodEnd)}.
        </p>
      )}
    </div>
  );
}

/** Monthly / annual segmented toggle for the switcher pricing + action. */
function CadenceToggle({
  cadence,
  onChange,
}: {
  cadence: Cadence;
  onChange: (c: Cadence) => void;
}) {
  return (
    <div className={styles.cadenceToggle} role="group" aria-label="Billing cadence">
      <button
        type="button"
        className={cadence === "monthly" ? styles.cadenceActive : styles.cadenceBtn}
        aria-pressed={cadence === "monthly"}
        onClick={() => onChange("monthly")}
      >
        Monthly
      </button>
      <button
        type="button"
        className={cadence === "annual" ? styles.cadenceActive : styles.cadenceBtn}
        aria-pressed={cadence === "annual"}
        onClick={() => onChange("annual")}
      >
        Annual
      </button>
    </div>
  );
}

/**
 * One self-serve tier card. Derives its action button from
 * classifyPlanChange(current → {thisPlan, selectedCadence}):
 *   - no active subscription → "Subscribe"
 *   - upgrade  → "Upgrade" (charges the prorated difference now)
 *   - downgrade→ "Downgrade" (takes effect at the period end)
 *   - noop     → "Current plan" (disabled)
 */
function TierCard({
  plan,
  cadence,
  sub,
  disabled,
  onAct,
}: {
  plan: PlanId;
  cadence: Cadence;
  sub: BillingState["subscription"];
  disabled: boolean;
  onAct: () => void;
}) {
  const p = PLANS[plan];
  const price = cadence === "annual" ? p.priceAnnual : p.priceMonthly;
  const priceSuffix = cadence === "annual" ? "/year" : "/month";

  // Classify only when there's an active subscription with a well-formed
  // current plan+cadence; otherwise this is a fresh subscribe.
  const kind =
    sub.active && sub.plan && sub.cadence
      ? classifyPlanChange(
          { plan: sub.plan, cadence: sub.cadence },
          { plan, cadence },
        )
      : null;

  let label: string;
  let note: string;
  let actDisabled = disabled;
  let variant: "primary" | "secondary" | "ghost" = "primary";

  if (kind === null) {
    label = "Subscribe";
    note = "Charges your saved card now.";
    variant = p.recommended ? "primary" : "secondary";
  } else if (kind === "noop") {
    label = "Current plan";
    note = "Your active plan.";
    actDisabled = true;
    variant = "ghost";
  } else if (kind === "upgrade") {
    label = "Upgrade";
    note = "Charges the prorated difference now.";
    variant = "primary";
  } else {
    label = "Downgrade";
    note = `Takes effect ${fmtDate(sub.currentPeriodEnd)}.`;
    variant = "secondary";
  }

  return (
    <div className={`${styles.tierCard} ${p.recommended ? styles.tierRecommended : ""}`}>
      <div className={styles.tierHead}>
        <span className={styles.tierName}>{p.name}</span>
        {p.recommended && <span className={styles.tierBadge}>Popular</span>}
      </div>
      <div className={styles.tierPrice}>
        {price != null ? dollars(price) : "—"}
        <span className={styles.tierPer}>{price != null ? priceSuffix : ""}</span>
      </div>
      <p className={styles.tierBlurb}>{p.blurb}</p>
      <Btn variant={variant} size="sm" full disabled={actDisabled} onClick={onAct}>
        {label}
      </Btn>
      <p className={styles.cardNote}>{note}</p>
    </div>
  );
}

/** Enterprise: sales-led, no self-serve price → "Contact sales", no API call. */
function EnterpriseTierCard() {
  const p = PLANS.enterprise;
  return (
    <div className={styles.tierCard}>
      <div className={styles.tierHead}>
        <span className={styles.tierName}>{p.name}</span>
      </div>
      <div className={styles.tierPrice}>
        Custom
      </div>
      <p className={styles.tierBlurb}>{p.blurb}</p>
      <Btn
        variant="secondary"
        size="sm"
        full
        href="mailto:sales@pheonixapps.com?subject=Enterprise%20plan%20enquiry"
      >
        Contact sales
      </Btn>
      <p className={styles.cardNote}>SSO, dedicated infra, and a contract.</p>
    </div>
  );
}

/** Confirm-cancel modal. Cancels at period end; the workspace then locks. */
function CancelModal({
  ws,
  sub,
  onClose,
}: {
  ws: string | undefined;
  sub: BillingState["subscription"];
  onClose: () => void;
}) {
  const cancel = useCancelSubscription(ws);

  function onConfirm() {
    cancel.mutate(undefined, { onSuccess: onClose });
  }

  return (
    <Modal
      open
      title="Cancel subscription"
      onClose={onClose}
      footer={
        <div className={styles.modalActions}>
          <Btn variant="ghost" size="sm" onClick={onClose}>
            Keep subscription
          </Btn>
          <Btn
            variant="destructive"
            size="sm"
            loading={cancel.isPending}
            onClick={onConfirm}
            iconLeft={<Icon name="x-circle" size={14} />}
          >
            Cancel at period end
          </Btn>
        </div>
      }
    >
      <p className={styles.modalBody}>
        Your workspace stays on {planName(sub.plan)} until{" "}
        {fmtDate(sub.currentPeriodEnd)}, then it locks until you re-subscribe.
      </p>
      {cancel.error && <p className={styles.error}>{cancel.error.message}</p>}
    </Modal>
  );
}

// ─── Media usage (existing) ─────────────────────────────────────────────────

function BillingStateCards({ billing }: { billing: BillingState }) {
  const modeLabel: Record<BillingState["mode"], string> = {
    "hard-cap": "Hard cap — generation stops at your included allowance",
    packs: "Prepaid packs — buy unit packs as you go",
    "no-cap": "Pay as you go — overage billed at month-end",
  };

  return (
    <>
      <h2 className={styles.sectionTitle}>Media usage</h2>
      <div className={styles.planRow}>
        <span className={styles.planMode}>{modeLabel[billing.mode]}</span>
      </div>

      <div className={styles.grid}>
        <UsageCard
          label="Photos"
          icon="image"
          used={billing.used.photo}
          allowance={billing.allowance.photo}
          remaining={billing.remaining.photo}
          pack={billing.packs.photo}
        />
        <UsageCard
          label="Videos"
          icon="play"
          used={billing.used.video}
          allowance={billing.allowance.video}
          remaining={billing.remaining.video}
          pack={billing.packs.video}
        />
        <div className={styles.card}>
          <div className={styles.cardHead}>
            <Icon name="store" size={15} />
            <span className={styles.cardLabel}>Accrued this month</span>
          </div>
          <div className={styles.accrued}>{centsToDollars(billing.accruedCents)}</div>
          <p className={styles.cardNote}>
            Overage you’ve accrued and owe at month-end (
            {centsToDollars(Math.round(billing.overagePricePerUnit * 100))}/unit).
            Not yet charged.
          </p>
        </div>
      </div>
    </>
  );
}

function UsageCard({
  label,
  icon,
  used,
  allowance,
  remaining,
  pack,
}: {
  label: string;
  icon: "image" | "play";
  used: number;
  allowance: number;
  remaining: number;
  pack: number;
}) {
  const pct = allowance > 0 ? Math.min(100, Math.round((used / allowance) * 100)) : 0;
  return (
    <div className={styles.card}>
      <div className={styles.cardHead}>
        <Icon name={icon} size={15} />
        <span className={styles.cardLabel}>{label}</span>
      </div>
      <div className={styles.usageNums}>
        <span className={styles.usageUsed}>{used}</span>
        <span className={styles.usageOf}> / {allowance} included</span>
      </div>
      <div className={styles.bar} aria-hidden="true">
        <span className={styles.barFill} style={{ width: `${pct}%` }} />
      </div>
      <p className={styles.cardNote}>
        {remaining} included unit{remaining === 1 ? "" : "s"} left this month
        {pack > 0 ? ` · ${pack} prepaid pack unit${pack === 1 ? "" : "s"}` : ""}
      </p>
    </div>
  );
}

function PaymentMethodSection({
  billing,
  isOwner,
  hasKey,
  onAdd,
}: {
  billing: BillingState;
  isOwner: boolean;
  hasKey: boolean;
  onAdd: () => void;
}) {
  // Payments not set up for this deployment at all — honest, no card UI.
  if (!billing.paymentsConfigured || !hasKey) {
    return (
      <section className={styles.payment}>
        <h2 className={styles.sectionTitle}>Payment method</h2>
        <p className={styles.muted}>
          Billing isn’t set up for this deployment yet, so payment methods can’t
          be added here. You can still generate within your included allowance.
        </p>
      </section>
    );
  }

  return (
    <section className={styles.payment}>
      <h2 className={styles.sectionTitle}>Payment method</h2>
      {billing.hasPaymentMethod ? (
        <div className={styles.cardOnFile}>
          <Icon name="check" size={15} />
          <span>A payment method is on file.</span>
        </div>
      ) : (
        <p className={styles.muted}>
          No payment method on file. Add a card to subscribe to a plan and to
          enable pay-as-you-go generation beyond your included allowance.
        </p>
      )}

      {isOwner ? (
        <Btn variant={billing.hasPaymentMethod ? "secondary" : "primary"} onClick={onAdd} iconLeft={<Icon name="store" size={14} />}>
          {billing.hasPaymentMethod ? "Update card" : "Add a card"}
        </Btn>
      ) : (
        <p className={styles.ownerNote}>Only a workspace owner can manage billing.</p>
      )}
    </section>
  );
}

/**
 * Add-card modal. Fetches a SetupIntent clientSecret (POST billing/payment-method),
 * then mounts Stripe Elements bound to it. Owner-only; the API re-checks.
 */
function AddPaymentMethodModal({
  ws,
  pk,
  onClose,
}: {
  ws: string | undefined;
  pk: string;
  onClose: () => void;
}) {
  const createPM = useCreatePaymentMethod(ws);
  const stripe = useMemo(() => getStripe(pk), [pk]);
  const [clientSecret, setClientSecret] = useState<string | undefined>();

  // Kick off the SetupIntent once when the modal mounts (it's only mounted
  // while open). A useEffect (not a render-phase call), plus a fired-once ref
  // guard so even StrictMode's double effect-invoke in dev can't create two
  // SetupIntents.
  const mutateRef = useRef(createPM.mutate);
  mutateRef.current = createPM.mutate;
  const firedRef = useRef(false);
  useEffect(() => {
    if (firedRef.current) return;
    firedRef.current = true;
    mutateRef.current(undefined, {
      onSuccess: (res) => setClientSecret(res.clientSecret),
    });
  }, []);

  return (
    <Modal open title="Add a payment method" onClose={onClose}>
      {createPM.error && (
        <p className={styles.error}>{createPM.error.message}</p>
      )}
      {!clientSecret && createPM.isPending && (
        <p className={styles.muted}>Preparing secure card form…</p>
      )}
      {clientSecret && (
        <Elements stripe={stripe} options={{ clientSecret }}>
          <CardForm onDone={onClose} />
        </Elements>
      )}
    </Modal>
  );
}

function CardForm({ onDone }: { onDone: () => void }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>();

  async function onSubmit() {
    if (!stripe || !elements) return;
    setSubmitting(true);
    setError(undefined);
    // confirmSetup saves the card to the customer (no charge). On success
    // Stripe fires a webhook the backend uses; we just close on confirmation.
    const result = await stripe.confirmSetup({
      elements,
      redirect: "if_required",
    });
    setSubmitting(false);
    if (result.error) {
      setError(result.error.message ?? "Couldn’t save the card. Please try again.");
      return;
    }
    onDone();
  }

  return (
    <div className={styles.cardForm}>
      <PaymentElement />
      {error && <p className={styles.error}>{error}</p>}
      <Btn
        variant="primary"
        full
        onClick={onSubmit}
        loading={submitting}
        disabled={!stripe || !elements}
        iconRight={<Icon name="check" size={14} />}
      >
        Save card
      </Btn>
      <p className={styles.cardFormNote}>
        Saving a card doesn’t charge it. We use it for your subscription and any
        media overage billed at month-end.
      </p>
    </div>
  );
}
